import React from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, TextField,
  MenuItem, Alert, Typography, Stack, Chip, Divider, Box, Checkbox, FormControlLabel,
} from "@mui/material";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import { StepUpSpinner } from "../../../../Components/Loader";
import { paymentsApi } from "./api";

type Props = {
  open: boolean;
  parentId: string | number;
  studentId: number;
  studentName: string;
  onClose: () => void;
  onSuccess: () => void;
};

type Invoice = { id: number; invoiceNumber: string; dueDate: string; totalAmount: number; balance_due: number };
type Payer = { parent_id: number; first_name: string; last_name: string; is_primary: boolean };
// Extended card shape — the /admin/parents/{id}/payment-methods
// endpoint already returns expiryMonth / expiryYear / holderName
// (see ParentPaymentAdminController::methods select list), so
// surfacing them here is a pure frontend read. Nothing PCI-sensitive
// (full PAN / CVV) is ever returned by the API and none is added.
type Card = {
  id: number;
  cardBrand: string | null;
  cardLast4: string | null;
  expiryMonth: number | null;
  expiryYear: number | null;
  holderName: string | null;
  status: string;
  isDefault: 0 | 1 | boolean;
};

const money = (n: any) => `$${Number(n || 0).toFixed(2)}`;

// "08/28" style expiry. Returns null when we don't have both parts so
// the caller can hide the label instead of rendering "--/--".
const fmtExpiry = (m: number | null, y: number | null): string | null => {
  if (m == null || y == null) return null;
  const mm = String(m).padStart(2, "0");
  const yy = String(y).slice(-2).padStart(2, "0");
  return `${mm}/${yy}`;
};

// Human-readable card brand label. Payment gateways return brands as
// short codes (MC / VI / AX / etc.); we normalise the common ones and
// pass unknown values through so a new brand never renders blank.
const prettyBrand = (raw: string | null): string => {
  const s = String(raw ?? "").trim().toUpperCase();
  if (!s) return "Card";
  const map: Record<string, string> = {
    MC: "Mastercard", MASTERCARD: "Mastercard",
    VI: "Visa", VISA: "Visa",
    AX: "Amex", AMEX: "Amex", "AMERICAN EXPRESS": "Amex",
    DI: "Discover", DISCOVER: "Discover",
    JCB: "JCB",
    DINERS: "Diners", "DINERS CLUB": "Diners",
  };
  return map[s] ?? (raw ?? "Card");
};

/**
 * Compact two-line card row used by both the dropdown MenuItem and
 * the collapsed field's renderValue so the selected state matches
 * the option list exactly.
 *
 *   Primary line   Mastercard •••• 4821            [Default] [Legacy]
 *   Secondary line Expires 08/28 · Kalakcika Dinesh
 *
 * The secondary line only renders the parts we actually have — no
 * empty "Expires --/--" or "Cardholder: —" fragments. Cardholder
 * names truncate with ellipsis so the row height stays constant.
 * PCI: only the last 4 digits ever appear here; the API does not
 * return a full PAN or CVV and we never render one.
 */
const CardRow: React.FC<{ c: Card; showMeta?: boolean }> = ({ c, showMeta }) => {
  const brand = prettyBrand(c.cardBrand);
  const expiry = fmtExpiry(c.expiryMonth, c.expiryYear);
  const holder = (c.holderName ?? "").trim();
  const secondary = [expiry ? `Expires ${expiry}` : null, holder || null].filter(Boolean).join(" · ");
  return (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0, width: "100%" }}>
      <Box sx={{
        width: 34, height: 24, borderRadius: 0.75,
        bgcolor: "#EEF2FF", color: "#4338CA",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <CreditCardOutlinedIcon sx={{ fontSize: 16 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: 14, fontWeight: 700, color: "#0F172A",
            lineHeight: 1.2,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}
          title={`${brand} •••• ${c.cardLast4 ?? "----"}`}
        >
          {brand} •••• {c.cardLast4 ?? "----"}
        </Typography>
        {secondary && (
          <Typography
            sx={{
              fontSize: 11.5, color: "#64748B",
              mt: 0.15, lineHeight: 1.25,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}
            title={secondary}
          >
            {secondary}
          </Typography>
        )}
      </Box>
      {showMeta && !!c.isDefault && (
        <Chip size="small" label="Default" color="primary" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, flexShrink: 0 }} />
      )}
      {showMeta && c.status === "legacy_unverified" && (
        <Chip size="small" label="Legacy — Verify Required" color="warning" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, flexShrink: 0 }} />
      )}
    </Stack>
  );
};

const MakePaymentDialog: React.FC<Props> = ({ open, parentId, studentId, studentName, onClose, onSuccess }) => {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [payers, setPayers] = React.useState<Payer[]>([]);
  const [invoices, setInvoices] = React.useState<Invoice[]>([]);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = React.useState<number[]>([]);
  const [payerId, setPayerId] = React.useState<number | "">("");
  const [cards, setCards] = React.useState<Card[]>([]);
  const [method, setMethod] = React.useState<"saved_card" | "bank_mandate" | "cash" | "bank_transfer" | "cheque" | "external_card" | "other">("saved_card");
  const [cardId, setCardId] = React.useState<number | "">("");
  const [bankMandates, setBankMandates] = React.useState<any[]>([]);
  const [mandateRowId, setMandateRowId] = React.useState<number | "">("");
  const [amount, setAmount] = React.useState<string>("");
  const [reference, setReference] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [paymentDate, setPaymentDate] = React.useState(new Date().toISOString().slice(0, 10));

  React.useEffect(() => {
    if (!open) return;
    setError(null);
    (async () => {
      try {
        const [pRes, iRes] = await Promise.all([
          paymentsApi.eligiblePayersForStudent(studentId),
          paymentsApi.openInvoicesForStudent(studentId),
        ]);
        setPayers(pRes?.data ?? []);
        setInvoices(iRes?.data ?? []);
        // Default payer to the currently-viewed parent if they are eligible;
        // otherwise fall back to the primary or the first eligible parent.
        const linkedNow = (pRes?.data ?? []).find((p: Payer) => String(p.parent_id) === String(parentId));
        const primary   = (pRes?.data ?? []).find((p: Payer) => p.is_primary);
        setPayerId(linkedNow?.parent_id ?? primary?.parent_id ?? pRes?.data?.[0]?.parent_id ?? "");
      } catch (e: any) {
        setError(e?.message ?? "Failed to load Make Payment data.");
      }
    })();
  }, [open, studentId, parentId]);

  React.useEffect(() => {
    if (!payerId) { setCards([]); setCardId(""); setBankMandates([]); setMandateRowId(""); return; }
    (async () => {
      try {
        const r = await paymentsApi.cardsForParent(payerId as number);
        setCards(r?.data ?? []);
        const dflt = (r?.data ?? []).find((c: Card) => c.isDefault);
        setCardId(dflt?.id ?? r?.data?.[0]?.id ?? "");
      } catch {
        setCards([]); setCardId("");
      }
      // Bank mandates (GoCardless). Endpoint 503s when feature flag is off —
      // treat that as "no mandates" rather than surfacing an error.
      try {
        const b = await paymentsApi.bankMandates(payerId as number);
        const list = (b?.data ?? []).filter((m: any) => m.mandate_status === "active" || m.mandate_status === "submitted");
        setBankMandates(list);
        const dflt = list.find((m: any) => m.is_default);
        setMandateRowId(dflt?.id ?? list?.[0]?.id ?? "");
      } catch {
        setBankMandates([]); setMandateRowId("");
      }
    })();
  }, [payerId]);

  // Auto-compute amount from selected invoices.
  React.useEffect(() => {
    const total = invoices.filter((i) => selectedInvoiceIds.includes(i.id))
      .reduce((s, i) => s + Number(i.balance_due || 0), 0);
    if (total > 0) setAmount(total.toFixed(2));
  }, [selectedInvoiceIds, invoices]);

  const canSubmit = !busy && payerId && Number(amount) > 0 && selectedInvoiceIds.length > 0 &&
    (method !== "saved_card" || !!cardId) &&
    (method !== "bank_mandate" || !!mandateRowId);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const allocations = invoices.filter((i) => selectedInvoiceIds.includes(i.id))
        .map((i) => ({ invoice_id: i.id, amount: Number(i.balance_due) }));
      const summed = allocations.reduce((s, a) => s + a.amount, 0);
      // If amount typed less than sum, prorate off the last invoice
      const amt = Number(amount);
      if (amt < summed) {
        allocations[allocations.length - 1].amount -= (summed - amt);
      }

      if (method === "saved_card") {
        const res = await paymentsApi.makeSavedCardPayment(parentId, {
          student_id: studentId,
          payment_method_id: cardId,
          amount: amt,
          allocations,
        });
        if (res?.status === "declined") { setError("Payment declined."); return; }
        if (res?.status === "requires_reconciliation") { setError("Payment requires reconciliation — no charge confirmed."); return; }
        if (res?.status === "unknown") { setError("Payment result unknown — reconciliation required."); return; }
      } else if (method === "bank_mandate") {
        // Async bank debit via GoCardless mandate. Returns status='pending' —
        // the invoice flips to Paid only when the webhook delivers payment.confirmed.
        const res = await paymentsApi.payWithBankMandate(parentId, {
          student_id: studentId,
          mandate_row_id: Number(mandateRowId),
          amount: amt,
          allocations,
        });
        if (res?.status === "pending") {
          // success path — invoice stays pending until webhook confirms
        }
      } else {
        await paymentsApi.makeManualPayment(parentId, {
          student_id: studentId,
          method,
          amount: amt,
          allocations,
          reference,
          notes,
          payment_date: paymentDate,
        });
      }
      onSuccess();
    } catch (e: any) {
      const msg = e?.message ?? "Payment failed.";
      setError(/not configured/i.test(msg) ? "Worldline payment gateway is not configured." : msg);
    } finally { setBusy(false); }
  };

  const outstanding = invoices.reduce((s, i) => s + Number(i.balance_due || 0), 0);

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="md" fullWidth
      disableEscapeKeyDown={busy}
      PaperProps={{ sx: { position: "relative", overflow: "hidden" } }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>
        Make Payment · {studentName}
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>Outstanding: {money(outstanding)}</Typography>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField select fullWidth label="Payer" value={payerId} onChange={(e) => setPayerId(Number(e.target.value))} disabled={busy || payers.length === 0}>
              {payers.map((p) => (
                <MenuItem key={p.parent_id} value={p.parent_id}>
                  {p.first_name} {p.last_name} {p.is_primary && <Chip size="small" label="Primary" sx={{ ml: 1 }} color="primary" />}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField select fullWidth label="Method" value={method} onChange={(e) => setMethod(e.target.value as any)} disabled={busy}>
              <MenuItem value="saved_card">Saved Card</MenuItem>
              {bankMandates.length > 0 && (
                <MenuItem value="bank_mandate">Bank Account — GoCardless PAD</MenuItem>
              )}
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="bank_transfer">Bank Transfer — Manual</MenuItem>
              <MenuItem value="cheque">Cheque</MenuItem>
              <MenuItem value="external_card">External Card</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
          </Grid>
          {method === "saved_card" && (
            <Grid item xs={12}>
              <TextField
                select fullWidth label="Card"
                value={cardId}
                onChange={(e) => setCardId(Number(e.target.value))}
                disabled={busy || cards.length === 0}
                helperText={cards.length === 0 ? "This payer has no saved cards. Add one from their profile first." : undefined}
                SelectProps={{
                  // Two-line premium layout when a card is selected —
                  // matches the MenuItem rows so the dropdown and the
                  // collapsed field read identically.
                  renderValue: (val) => {
                    const c = cards.find((x) => x.id === Number(val));
                    if (!c) return "";
                    return <CardRow c={c} />;
                  },
                }}
              >
                {cards.map((c) => (
                  <MenuItem key={c.id} value={c.id} sx={{ py: 1 }}>
                    <CardRow c={c} showMeta />
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          {method === "bank_mandate" && (
            <Grid item xs={12}>
              <TextField
                select fullWidth label="Bank Account (GoCardless PAD)"
                value={mandateRowId}
                onChange={(e) => setMandateRowId(Number(e.target.value))}
                disabled={busy || bankMandates.length === 0}
                helperText="Debit will be submitted to GoCardless. Invoice will remain pending until the bank confirms the debit (typically within a few business days)."
              >
                {bankMandates.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    Bank Account {m.account_last4 ? `•••• ${m.account_last4}` : ""} — {m.scheme?.toUpperCase() ?? "PAD"} ({m.mandate_status})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          <Grid item xs={12}>
            <Divider>Invoices</Divider>
            {invoices.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No open invoices.</Typography>
            )}
            <Box sx={{ mt: 1 }}>
              {invoices.map((inv) => (
                <FormControlLabel key={inv.id}
                  control={<Checkbox
                    checked={selectedInvoiceIds.includes(inv.id)}
                    onChange={(e) => {
                      setSelectedInvoiceIds(e.target.checked
                        ? [...selectedInvoiceIds, inv.id]
                        : selectedInvoiceIds.filter((x) => x !== inv.id));
                    }}
                    disabled={busy}
                  />}
                  label={<Stack direction="row" spacing={2}><span>{inv.invoiceNumber}</span><span style={{ color: "#666" }}>Due {inv.dueDate}</span><strong>{money(inv.balance_due)}</strong></Stack>}
                />
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} disabled={busy} />
          </Grid>

          {method !== "saved_card" && method !== "bank_mandate" && (
            <>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Payment Date" type="date" InputLabelProps={{ shrink: true }} value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} disabled={busy} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Reference" value={reference} onChange={(e) => setReference(e.target.value)} disabled={busy} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Notes" multiline minRows={2} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={busy} />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!canSubmit}>
          {busy ? "Processing…" : (method === "saved_card" ? "Charge Card" : method === "bank_mandate" ? "Submit Bank Debit" : "Record Payment")}
        </Button>
      </DialogActions>
      {busy && (
        <Box
          aria-live="polite" aria-busy="true"
          sx={{
            position: "absolute", inset: 0, zIndex: 10,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            gap: 1.5,
            bgcolor: "rgba(255,255,255,0.86)",
            backdropFilter: "blur(3px)",
            WebkitBackdropFilter: "blur(3px)",
          }}
        >
          <StepUpSpinner size="large" />
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary", mt: 1 }}>
            {method === "saved_card" ? "Charging your card…" : "Recording your payment…"}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            {method === "saved_card"
              ? "Please wait while we connect securely with Worldline."
              : "Please wait while we save the payment."}
          </Typography>
        </Box>
      )}
    </Dialog>
  );
};

export default MakePaymentDialog;
